/** Hooks pour la gestion des sites. */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const API_URL = import.meta.env.VITE_API_URL;

function headers(): HeadersInit {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
}

export interface Site {
  id: number;
  name: string;
  address?: string | null;
  is_active?: boolean;
  employees_count?: number;
  readers_count?: number;
}

export interface SitePayload {
  name: string;
  address?: string | null;
}

export function useSites() {
  return useQuery({
    queryKey: ['sites'],
    queryFn: async (): Promise<Site[]> => {
      const res = await fetch(`${API_URL}/api/sites`, { headers: headers() });
      return res.json();
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateSite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: SitePayload) => {
      const res = await fetch(`${API_URL}/api/sites`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Erreur');
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sites'] }),
  });
}

export function useUpdateSite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<SitePayload> }) => {
      const res = await fetch(`${API_URL}/api/sites/${id}`, {
        method: 'PUT',
        headers: headers(),
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Erreur');
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sites'] }),
  });
}

export function useDeleteSite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`${API_URL}/api/sites/${id}`, {
        method: 'DELETE',
        headers: headers(),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Erreur');
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sites'] }),
  });
}
